import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { submitPayment } from '../../services/payment';
import { useScrollToTopOnError } from '../../utils/scrollUtils';

const PaymentSubmission = () => {
  const { profile, getUserLevel } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [fileUploading, setFileUploading] = useState(false);
  const [formData, setFormData] = useState({
    amount: '',
    phoneNumber: '',
    transactionId: '',
    paymentDate: '',
    notes: '',
    affiliateCode: ''
  });
  const [proofFile, setProofFile] = useState(null);
  const [error, setError] = useState('');

  // Scroll to top when errors occur
  useScrollToTopOnError(error);
  useScrollToTopOnError(uploadError);
  const [success, setSuccess] = useState(false);

  const getLevelInfo = () => {
    const levels = {
      grade7: { name: 'Grade 7', price: 'K500', number: '1' },
      form2: { name: 'Form 2', price: 'K1000', number: '2' },
      grade12: { name: 'Grade 12', price: 'K1500', number: '3' }
    };
    return levels[getUserLevel()] || { name: 'Unknown', price: 'K0', number: '0' };
  };

  const levelInfo = getLevelInfo();

  React.useEffect(() => {
    // Set default amount
    setFormData(prev => ({
      ...prev,
      amount: levelInfo.price
    }));
  }, [levelInfo]);
  
  // Don't render until profile is loaded
  if (!profile) {
    return (
      <div className="payment-submission">
        <div className="container">
          <div className="loading">Loading...</div>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload an image (JPG/PNG) or PDF file');
        return;
      }
      
      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        setError('File size must be less than 5MB');
        return;
      }
      
      setProofFile(file);
      setError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      // Validate required fields
      if (!formData.phoneNumber || !formData.transactionId || !formData.paymentDate) {
        setError('Please fill in all required fields');
        setSubmitting(false);
        return;
      }

      // Check if phone number is exactly 10 digits
      const cleanedPhone = formData.phoneNumber.replace(/\s/g, '');
      if (cleanedPhone.length !== 10 || !/^\d+$/.test(cleanedPhone)) {
        setError('Please enter a valid 10-digit phone number');
        setSubmitting(false);
        return;
      }

      let proofOfPaymentUrl = null;

      // Upload proof file if provided
      if (proofFile) {
        try {
          setFileUploading(true);
          // For now, just simulate upload
          proofOfPaymentUrl = 'https://example.com/proof';
          setFileUploading(false);
        } catch (uploadErr) {
          setUploadError('Failed to upload proof of payment. Please try again.');
          setSubmitting(false);
          setFileUploading(false);
          return;
        }
      }

      // Submit payment
      const paymentData = {
        userId: profile.uid,
        levelId: getUserLevel(),
        amount: parseInt(levelInfo.price.replace('K', '')),
        phoneNumber: formData.phoneNumber,
        transactionId: formData.transactionId,
        paymentDate: new Date(formData.paymentDate),
        proofOfPaymentUrl,
        notes: formData.notes
      };

      await submitPayment(paymentData);
      
      setSuccess(true);
      setSubmitting(false);
    } catch (err) {
      setError('Failed to submit payment. Please try again.');
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="payment-submission">
        <div className="container">
          <div className="success-message">
            <div className="success-icon">✅</div>
            <h2>Payment Submitted Successfully!</h2>
            <p>Your payment has been submitted for review. We'll notify you once it's approved.</p>
            <div className="success-details">
              <p><strong>Transaction ID:</strong> {formData.transactionId}</p>
              <p><strong>Amount:</strong> {levelInfo.price}</p>
              <p><strong>Status:</strong> Pending Review</p>
            </div>
            <div className="success-actions">
              <Link to="/dashboard" className="btn btn-primary">
                Back to Dashboard
              </Link>
              <Link to="/payment/status" className="btn btn-secondary">
                Check Payment Status
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="payment-submission">
      <div className="container">
        <div className="page-header">
          <h1>Submit Payment Proof</h1>
          <p>Provide your payment details for verification</p>
        </div>

        <div className="payment-form-card">
          <div className="form-header">
            <h3>Payment Information</h3>
            <p>Please fill in the details from your mobile money transaction</p>
          </div>

          {error && <div className="error-message">{error}</div>}
          {uploadError && <div className="error-message">{uploadError}</div>}

          <form onSubmit={handleSubmit} className="payment-form">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="phoneNumber">Phone Number *</label>
                <input
                  type="tel"
                  id="phoneNumber"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  placeholder="0779518400"
                  required
                />
                <small>Enter the phone number used for the payment</small>
              </div>

              <div className="form-group">
                <label htmlFor="transactionId">Transaction ID *</label>
                <input
                  type="text"
                  id="transactionId"
                  name="transactionId"
                  value={formData.transactionId}
                  onChange={handleChange}
                  placeholder="e.g., MM123456789"
                  required
                />
                <small>Transaction reference from your mobile money provider</small>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="paymentDate">Payment Date *</label>
                <input
                  type="date"
                  id="paymentDate"
                  name="paymentDate"
                  value={formData.paymentDate}
                  onChange={handleChange}
                  max={new Date().toISOString().split('T')[0]}
                  required
                />
                <small>Date when you made the payment</small>
              </div>

              <div className="form-group">
                <label htmlFor="amount">Amount (ZMW) *</label>
                <input
                  type="text"
                  id="amount"
                  name="amount"
                  value={formData.amount}
                  readOnly
                  className="readonly"
                />
                <small>Subscription fee for {levelInfo.name}</small>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="proofFile">Proof of Payment (Optional)</label>
              <input
                type="file"
                id="proofFile"
                onChange={handleFileChange}
                accept="image/jpeg,image/jpg,image/png,application/pdf"
              />
              <small>Upload screenshot of your transaction confirmation (JPG/PNG/PDF, max 5MB)</small>
            </div>

            <div className="form-group">
              <label htmlFor="affiliateCode">Promo Code (Optional)</label>
              <input
                type="text"
                id="affiliateCode"
                name="affiliateCode"
                value={formData.affiliateCode}
                onChange={handleChange}
                placeholder="Enter promo code for 20% discount"
                style={{ textTransform: 'uppercase' }}
              />
              <small>If you have a promo code, enter it here to get a 20% discount</small>
            </div>

            <div className="form-group">
              <label htmlFor="notes">Additional Notes</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                rows="4"
                placeholder="Any additional information about your payment..."
              ></textarea>
            </div>

            <div className="form-summary">
              <h4>Payment Summary</h4>
              <div className="summary-item">
                <label>Level:</label>
                <span>{levelInfo.name}</span>
              </div>
              <div className="summary-item">
                <label>Amount:</label>
                <span className="price">{levelInfo.price}</span>
              </div>
              <div className="summary-item">
                <label>Your Reference:</label>
                <span className="reference">ZS{levelInfo.number}{profile?.uid?.slice(-6)}</span>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary btn-large"
                disabled={submitting || fileUploading}
              >
                {submitting || fileUploading ? 'Submitting...' : 'Submit Payment'}
              </button>
              <Link to="/payment/instructions" className="btn btn-secondary">
                Back to Instructions
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default PaymentSubmission;
